/**
 * Participant-facing copy, transcribed verbatim from
 * docs/03_MOBILE_UI_SPEC.md (§5, §6, §9, §10, §13–§19, §28) and the approved
 * design in design/. Nothing here is newly authored: the wording was signed
 * off with the spec, so components import strings rather than inlining them.
 *
 * Error copy for server responses lives in @irace/shared/messages, because
 * the API sends the same strings.
 *
 * One exception, marked below: `home` was added after sign-off, on the
 * organizer's instruction, for a screen the spec does not describe.
 */

export const COPY = {
  /**
   * The landing screen at `/`. Not in the UI spec — added on request because
   * a bare domain used to answer "Không tìm thấy sự kiện.", which reads as a
   * broken site to anyone who types the address instead of scanning a code.
   */
  home: {
    tagline: 'Hệ thống checkin qua QR',
  },

  /** M00 — bootstrap loading (§5). */
  loading: {
    title: 'Đang tải...',
    offlineFirstLoad: 'Không thể tải sự kiện khi thiết bị đang mất kết nối.',
    retry: 'Vui lòng thử lại',
  },

  /** M01 — event unavailable (§6, §28.3). */
  unavailable: {
    scheduled: 'Check-in chưa mở.',
    scheduledSub: 'Vui lòng quay lại khi sự kiện bắt đầu.',
    closed: 'Check-in đã kết thúc.',
    closedSub: 'Cảm ơn bạn đã tham gia sự kiện.',
    notFound: 'Không tìm thấy sự kiện.',
    notFoundSub: 'Vui lòng kiểm tra lại đường dẫn hoặc quét lại mã QR.',
    /** §28.3 — omitted entirely when starts_at is null. */
    opensAtPrefix: 'Check-in mở lúc',
  },

  /** M02 — check-in form (§7, design). */
  form: {
    heading: 'CHECK-IN',
    locationLabel: 'Địa điểm',
    locationPlaceholder: 'Chọn địa điểm check-in',
    /** Shown instead of the picker when the event has exactly one venue. */
    locationFixedLabel: 'Địa điểm',
    /**
     * Second exception to the note at the top of this file: the three strings
     * below were authored after sign-off, for the per-location URL the spec
     * does not describe (`/e/:slug/:code`).
     */
    locationLockedLabel: 'Bạn đang check-in tại',
    locationChange: 'Đổi địa điểm',
    locationNotFound: 'Không tìm thấy địa điểm trong đường dẫn. Vui lòng chọn địa điểm bên dưới.',
    submit: 'CHECK-IN',
    submitting: 'ĐANG GỬI...',
    turnstileNotice: 'Được bảo vệ bởi Cloudflare Turnstile',
    privacy: 'Thông tin chỉ dùng để ghi nhận check-in sự kiện.',
    support: 'Cần hỗ trợ? Liên hệ ban tổ chức tại điểm check-in.',
  },

  /** Validation (§9, §28.4). */
  validation: {
    requiredLocation: 'Vui lòng chọn địa điểm.',
    requiredPrefix: 'Vui lòng nhập',
    invalidEmail: 'Vui lòng nhập email hợp lệ.',
    invalidPhone: 'Số điện thoại phải bắt đầu bằng 0 và có 10 hoặc 11 chữ số.',
    tooLongPrefix: 'Tối đa',
    tooLongSuffix: 'ký tự.',
  },

  /** M03 — location picker (§10, design). */
  picker: {
    title: 'Chọn địa điểm',
    searchPlaceholder: 'Tìm địa điểm...',
    close: 'Đóng',
    hintPrefix: 'Hỗ trợ tìm không dấu: "hcm", "sai gon", "da nang"',
    empty: 'Không tìm thấy địa điểm phù hợp.',
    locationCountSuffix: 'địa điểm',
  },

  /** M04 — sending (§14). */
  sending: {
    title: 'Đang gửi...',
    keepOpen: 'Vui lòng giữ trang mở.',
    /** §13 — never claim success while the Queue has accepted nothing. */
    verifying: 'Đang xác minh...',
  },

  /** M05 — offline saved / pending (§15). Amber, never green. */
  pending: {
    title: 'ĐÃ LƯU TRÊN THIẾT BỊ',
    line1: 'Mạng hiện không ổn định. Thông tin chưa được máy chủ xác nhận.',
    line2: 'Hệ thống sẽ thử gửi lại khi kết nối ổn định.',
    retryNow: 'GỬI LẠI NGAY',
    offlineHint: 'Chưa có kết nối',
  },

  /**
   * §12 — the local write failed, so nothing was sent. Not in the §25 error
   * table because it never reaches the server: the browser refused to store
   * anything, which on a phone means private browsing or a full device.
   */
  saveFailed: {
    body: 'Chưa lưu được thông tin trên thiết bị nên chưa thể gửi. Vui lòng tắt chế độ duyệt web riêng tư rồi thử lại.',
  },

  /** M06 — Turnstile retry (§16). */
  verifyRetry: {
    title: 'Không thể xác minh yêu cầu.',
    action: 'XÁC MINH & GỬI LẠI',
  },

  /** M07 — accepted (§17). Reached only after HTTP 202. */
  /**
   * Third exception to the note at the top of this file: the event info panel
   * below the check-in button is not in the UI spec. `organizerHeading` and
   * `eventPageLink` repeat `accepted` word for word on purpose. A participant
   * sees both screens, and two names for the same thing would read as two
   * different products.
   */
  eventInfo: {
    scheduleLabel: 'Thời gian',
    /** Prefix for an event whose organizer stated only a closing time. */
    scheduleEndPrefix: 'Kết thúc',
    organizerHeading: 'Ban tổ chức',
    eventPageLink: 'Xem trang sự kiện',
  },

  accepted: {
    title: 'ĐÃ CHECK-IN',
    body: 'Thông tin của bạn đã được ghi nhận.',
    /** Prefix for the check-in time, shown only on a screen restored later. */
    atLabel: 'Lúc',
    /**
     * The way back to a blank form on a restored screen.
     *
     * Says who the next check-in is for, because the screen it sits on is
     * green and says ĐÃ CHECK-IN: a control labelled only "check-in" there
     * reads as "do it again", which is the duplicate this screen exists to
     * prevent.
     */
    checkinAgain: 'Check-in cho người khác',
    locationLabel: 'Địa điểm',
    organizerHeading: 'Ban tổ chức',
    eventPageLink: 'Xem trang sự kiện',
  },

  /**
   * Optional check-out (G-014). Only rendered when the bootstrap carries a
   * `checkout` block, so an event without it sees none of this.
   *
   * The first three are *defaults*: an organizer who typed their own wording
   * replaces them, which is why the panel falls back to these per string
   * rather than per block. The rest are the device's own status wording and
   * are never organizer-editable — a participant must not be told "đã ghi
   * nhận" by wording an organizer chose for a departure the server has not
   * confirmed (AGENTS.md invariant 5).
   */
  checkout: {
    notice: 'Sự kiện này có ghi nhận giờ check-out.',
    button: 'Check-out',
    keepOpenHint: 'Đừng đóng cửa sổ này. Lúc ra về, mở lại và bấm check-out.',
    sending: 'Đang gửi...',
    pending: 'Đã lưu trên máy. Sẽ gửi khi có mạng trở lại.',
    done: 'Đã ghi nhận giờ về.',
    failed: 'Chưa gửi được. Vui lòng thử lại.',
    resumeHeading: 'Bạn đã check-in',
    resumeAt: 'Lúc',
  },

  /** Pending recovery on reopen (§18, §19). */
  recovery: {
    single: 'Bạn có 1 lượt check-in chưa được máy chủ xác nhận.',
    manyPrefix: 'Đang chờ đồng bộ:',
    action: 'XÁC MINH & GỬI NGAY',
  },

  /**
   * K-002 — a new bundle is waiting. Shown only when nothing is in flight, so
   * the wording is an offer, not an interruption.
   */
  update: {
    body: 'Đã có bản cập nhật.',
    action: 'CẬP NHẬT',
  },

  /** §28.1 — every screen except M00. */
  footer: {
    poweredBy: 'Check-in cung cấp bởi',
  },
} as const;

/** §9 — "Vui lòng nhập họ và tên." from the configured field label. */
export function requiredFieldMessage(label: string): string {
  return `${COPY.validation.requiredPrefix} ${label.toLowerCase()}.`;
}

export function maxLengthMessage(maxLength: number): string {
  return `${COPY.validation.tooLongPrefix} ${String(maxLength)} ${COPY.validation.tooLongSuffix}`;
}
